/**
 * English copy. This is the default and fallback locale. Legal sections are
 * translated faithfully; the German original remains authoritative. Every key
 * here must also exist in every other dictionary; `npm run check` enforces that.
 */

export default {
  // ── Navigation, chrome and shared calls to action ─────────────
  'a11y.language': 'Choose language',
  'a11y.mainNav': 'Main navigation',
  'a11y.breadcrumb': 'Breadcrumb',
  'a11y.noScript': 'JavaScript is disabled. The contact form and language selection need JavaScript. Contact:',
  'a11y.skipToContent': 'Skip to content',
  'a11y.footerNav': 'Footer navigation',
  'cta.button': 'Discuss a project',
  'cta.contact': 'Discuss a project',
  'cta.label': 'Next step',
  'cta.lede': 'Tell us about your game, app, website or AI project. A short message is enough to get started.',
  'cta.start': 'Start a project',
  'cta.title': 'What should exist next?',
  'nav.backHome': 'Back to home',
  'nav.blog': 'Blog',
  'nav.contact': 'Contact',
  'nav.games': 'Games',
  'nav.home': 'Studio',
  'nav.imprint': 'Imprint',
  'nav.lab': 'Lab',
  'nav.menu.close': 'Close menu',
  'nav.menu.open': 'Open menu',
  'nav.press': 'Press',
  'nav.privacy': 'Privacy',
  'nav.services': 'Services',
  'nav.sustainability': 'Sustainability',
  'nav.terms': 'Terms',
  'state.live': 'Live',
  'state.upcoming': 'In development',
  'state.training': 'In training',
  'store.ios': 'App Store',
  'store.openIn': 'Open {title} on {store}',
  'store.play': 'Google Play',
  'footer.backToTop': 'Back to top',
  'footer.built': 'Built in Stralsund, Germany.',
  'footer.company': 'Company',
  'footer.contact': 'Contact',
  'footer.legal': 'Legal',
  'footer.nav': 'Navigation',
  'footer.status': '{live} titles live · {upcoming} in progress',
  'footer.tagline': 'Independent technology studio for games, apps, websites and AI.',

  // ── Page metadata ─────────────────────────────────────────────
  'meta.blog.description': 'Notes from the NETBYTE studio in Stralsund: what we build, how we measure it and what went wrong along the way.',
  'meta.blog.title': 'Blog – NETBYTE',
  'meta.contact.description': 'Get in touch with NETBYTE in Stralsund about a game, app, website or AI project. We reply personally.',
  'meta.contact.title': 'Contact – NETBYTE',
  'meta.games.description': 'The NETBYTE catalogue: puzzle, idle, strategy and survivor titles for iOS, Android and PC – seven releases and one game in development.',
  'meta.games.title': 'Games – NETBYTE',
  'meta.home.description': 'Independent technology studio in Stralsund: seven games in the stores, apps and websites for clients, and our own language model and search engine.',
  'meta.home.title': 'NETBYTE – Games, apps, web and AI from Stralsund',
  'meta.imprint.description': 'Legal notice and provider information for NETBYTE UG (haftungsbeschränkt), Stralsund.',
  'meta.imprint.title': 'Imprint – NETBYTE',
  'meta.lab.description': 'The NETBYTE lab: Helix, a language model for all 24 official EU languages, and Mira, a search engine with its own crawler and index.',
  'meta.lab.title': 'Lab – NETBYTE',
  'meta.press.description': 'Press contact, company profile and press material for NETBYTE UG (haftungsbeschränkt) in Stralsund.',
  'meta.press.title': 'Press – NETBYTE',
  'meta.privacy.description': 'NETBYTE UG privacy policy: website, contact form, apps, advertising and in-app purchases.',
  'meta.privacy.title': 'Privacy Policy – NETBYTE',
  'meta.services.description': 'Games, apps, websites, AI and search: from concept and prototype to release, publishing and care.',
  'meta.services.title': 'Services – NETBYTE',
  'meta.sustainability.description': 'How NETBYTE handles resources: digital distribution, a lean website and transparent limits.',
  'meta.sustainability.title': 'Sustainability – NETBYTE',
  'meta.terms.description': 'General terms and conditions of NETBYTE UG for software, game, app and web projects.',
  'meta.terms.title': 'Terms of Service – NETBYTE',

  // ── Home ──────────────────────────────────────────────────────
  'home.hero.eyebrow': 'Independent tech studio · Stralsund',
  'home.hero.titleTop': 'Built to be',
  'home.hero.titleAccent': 'used.',
  'home.hero.lede': 'NETBYTE builds software that people actually use: games in the stores, apps and websites for clients – and technology of our own, from a language model for all 24 official EU languages to a search engine.',
  'home.hero.primary': 'Explore the lab',
  'home.hero.secondary': 'Discuss a project',
  'home.hero.live': '{count} games live',
  'home.hero.lab': '{count} in the lab',
  'home.studio.label': 'The studio',
  'home.studio.statement1': 'Good software does not end at the ',
  'home.studio.statement2': 'prototype.',
  'home.studio.text1': 'We work in small, traceable steps: understand the problem first, then build the simplest good solution. If it is ready to ship, we ship it. If it is not, we do not let it sit.',
  'home.studio.text2': 'That applies to our games, our research and client work alike. No agency theatre, no pitch decks gathering dust. Just work you can use when it is done.',
  'home.stats.titles': 'Games in the stores',
  'home.stats.upcoming': 'Titles in progress',
  'home.stats.lab': 'Lab projects',
  'home.stats.disciplines': 'Disciplines',
  'home.stats.platforms': 'Platforms',
  'home.lab.label': 'Lab',
  'home.lab.title': 'Technology of our own.',
  'home.lab.lede': 'Alongside games and client work we build our own foundations: a language model and a search engine. Both are still in development – and we write openly about how it is going.',
  'home.lab.all': 'Visit the lab',
  'home.works.label': 'Games',
  'home.works.title': 'From the shelf.',
  'home.works.lede': 'Four titles that show how different our work can sound: a numbers puzzle, two idle worlds – and a neon survivor for PC.',
  'home.works.all': 'See all eight titles',
  'home.services.label': 'Services',
  'home.services.title': 'From game to language model.',
  'home.services.lede': 'Five disciplines, one standard: finish the thing. For our own ideas and for client projects.',
  'home.services.all': 'All services in detail',

  // ── Shared process ───────────────────────────────────────────
  'process.label': 'How we work',
  'process.title': 'Four steps, no black box.',
  'process.lede': 'A process is not a ritual. It makes sure everyone knows where the project stands – and what happens next.',
  'process.step1.title': 'Set the frame',
  'process.step1.description': 'Goals, audience and scope are written down before the first line of code. A clear no is better than a vague maybe.',
  'process.step2.title': 'Make it tangible',
  'process.step2.description': 'A clickable prototype or playable build shows early whether the direction is right. Early feedback is cheaper than late corrections.',
  'process.step3.title': 'Build and refine',
  'process.step3.description': 'Feature by feature, with short feedback loops, clean code and a close eye on performance and usability.',
  'process.step4.title': 'Ship and stay',
  'process.step4.description': 'Store, deployment, measurement, iteration. A product is only done when it is out there – and it stays in care afterwards.',

  // ── Catalogue: games (app information, kept as-is) ───────────
  'game.cosmic-synthesis.description': 'Idle space adventure: discover elements and grow your cosmic lab.',
  'game.cosmic-synthesis.title': 'Cosmic Synthesis',
  'game.eco-tycoon.description': 'Build your eco-friendly empire in this strategy game focused on sustainability.',
  'game.eco-tycoon.title': 'Eco Tycoon',
  'game.idle-archive.description': 'Rebuild lost knowledge in a retro idle clicker with prestige and upgrades.',
  'game.idle-archive.title': 'Idle Archive',
  'game.neon-syntax.description': 'Retro survivor action: outlast endless waves of glitching code in a neon-lit arena.',
  'game.neon-syntax.title': 'Neon Syntax',
  'game.nexion.description': 'Addictive number-matching puzzle. Simple to learn, hard to put down.',
  'game.nexion.title': 'Nexion: Numbers Matching',
  'game.power-idle.description': 'Energy management idle: build power plants, balance the grid and keep the city lit.',
  'game.power-idle.title': 'Power Idle',
  'game.shadow-node.description': 'Hack your way to digital supremacy in this addictive idle hacker game.',
  'game.shadow-node.title': 'Shadow Node',
  'game.sudoku-rush.description': 'Classic Sudoku with time race, daily puzzles & boosters!',
  'game.sudoku-rush.title': 'Sudoku Rush',
  'genre.idle': 'Idle',
  'genre.puzzle': 'Puzzle',
  'genre.strategy': 'Strategy',
  'genre.survivor': 'Survivor',
  'tag.cyberpunk': 'Cyberpunk',
  'tag.daily': 'Daily',
  'tag.idle': 'Idle',
  'tag.management': 'Management',
  'tag.numbers': 'Numbers',
  'tag.puzzle': 'Puzzle',
  'tag.retro': 'Retro',
  'tag.space': 'Space',
  'tag.strategy': 'Strategy',
  'tag.survivor': 'Survivor',

  // ── Games page ───────────────────────────────────────────────
  'games.label': 'Catalogue',
  'games.title': 'Games we have built.',
  'games.lede': 'Eight titles from one studio: seven are released, one is in the works for PC. From a quick numbers puzzle to a slowly growing eco empire.',
  'games.upcoming.label': 'In development',
  'games.upcoming.note': 'The next release',
  'games.upcoming.cta': 'Talk about Neon Syntax',
  'games.library.label': 'Released',
  'games.library.title': 'Every title in the stores.',
  'games.library.lede': 'Every title is small enough to truly master and big enough to keep you for a while. Filter by genre or browse the whole catalogue.',
  'games.filter.label': 'Filter by genre',
  'games.filter.all': 'All',
  'games.filter.count': 'Titles: {count}',
  'games.cta.label': 'Your idea?',
  'games.cta.title': 'The next game could be yours.',
  'games.cta.lede': 'We help with game design, prototyping and the road into the stores. Tell us what is on your mind.',
  'games.cta.button': 'Discuss a game project',

  // ── Lab ──────────────────────────────────────────────────────
  'lab.label': 'Lab',
  'lab.title': 'Technology we build for ourselves.',
  'lab.lede': 'Besides games and client work, NETBYTE develops its own foundations: a language model for Europe\'s languages and an independent search engine. Neither is finished yet. This page shows where they stand.',
  'lab.count': 'Projects in development',
  'lab.blog': 'Read the development notes',
  'lab.helix.kind': 'Language model',
  'lab.helix.description': 'A language model with 320 million parameters, trained from scratch for all 24 official EU languages – on rented gaming GPUs instead of a data centre. We document every step, including the mistakes.',
  'lab.helix.fact1': '320 M parameters',
  'lab.helix.fact2': '24 official EU languages',
  'lab.helix.fact3': '6.5 B training tokens',
  'lab.helix.cta': 'Read part 1',
  'lab.helix.alt': 'Dot pattern on a dark background: which earlier words the Helix model can look back at.',
  'lab.mira.kind': 'Search engine',
  'lab.mira.description': 'An independent web search with its own crawler and its own index. Mira respects robots.txt and sitemaps, waits politely between requests, filters out duplicates and ranks results by language and country. It is still an early prototype.',
  'lab.mira.fact1': 'Own crawler',
  'lab.mira.fact2': 'Own full-text index',
  'lab.mira.fact3': 'Language-aware ranking',
  'lab.mira.cta': 'Talk to us about search',
  'lab.mira.alt': 'Radial tree on a dark background: the domains Mira discovered in its first crawls, ring by ring from the starting page.',
  'lab.why.label': 'Why we do it',
  'lab.why.title': 'Three reasons to build it ourselves.',
  'lab.why1.title': 'Know-how, not black boxes',
  'lab.why1.text': 'Having trained a model and run a crawler ourselves, we can judge what an AI or search product really needs – and what it does not.',
  'lab.why2.title': 'Europe\'s languages',
  'lab.why2.text': 'Smaller languages get little attention from the big models. Helix treats all 24 official EU languages as first-class from the start.',
  'lab.why3.title': 'Measured in public',
  'lab.why3.text': 'We publish numbers, costs and mistakes on the blog. That keeps us honest, and others can learn from it.',
  'lab.cta.label': 'Shared interest?',
  'lab.cta.title': 'Building something with AI or search?',
  'lab.cta.lede': 'Whether you need a search for your own content or want to know what a small language model can do: tell us what you are planning.',
  'lab.cta.button': 'Discuss an AI project',

  // ── Services ─────────────────────────────────────────────────
  'services.label': 'Services',
  'services.title': 'From first sketch to final build.',
  'services.lede': 'We build games, apps, websites and AI systems – on our own or alongside your team. Five disciplines, one well-rehearsed process.',
  'services.tracks.label': 'What we do',
  'services.tracks.title': 'Five disciplines.',
  'services.tracks.lede': 'Each is solid on its own. Together they make products that do not fall apart at the seams.',
  'services.mobile-games.title': 'Mobile Games',
  'services.mobile-games.description': 'Games for iOS and Android that work in your hand: a clear loop, honest progression, a clean store release.',
  'services.mobile-games.feature1': 'Game design & prototyping',
  'services.mobile-games.feature2': 'Unity and native development',
  'services.mobile-games.feature3': 'Google Play & App Store',
  'services.mobile-games.feature4': 'Updates, events & live ops',
  'services.pc-games.title': 'PC Games',
  'services.pc-games.description': 'Bigger worlds for desktop: technically solid, focused in play and built for a real release.',
  'services.pc-games.feature1': 'Unity & Unreal Engine',
  'services.pc-games.feature2': 'Steam preparation & publishing',
  'services.pc-games.feature3': 'Performance & engineering',
  'services.pc-games.feature4': 'Multiplayer & netcode',
  'services.mobile-apps.title': 'Mobile Apps',
  'services.mobile-apps.description': 'Apps that actually solve a task – native or cross-platform, with a reliable route to release.',
  'services.mobile-apps.feature1': 'Native iOS & Android development',
  'services.mobile-apps.feature2': 'React Native & Flutter',
  'services.mobile-apps.feature3': 'APIs, backends & integrations',
  'services.mobile-apps.feature4': 'Store submission & care',
  'services.websites.title': 'Websites',
  'services.websites.description': 'Sites and web apps that load fast, look right and stay maintainable after launch.',
  'services.websites.feature1': 'Design & frontend',
  'services.websites.feature2': 'React, Vue & modern stacks',
  'services.websites.feature3': 'CMS & e-commerce',
  'services.websites.feature4': 'SEO & performance',
  'services.ai-search.title': 'AI & Search',
  'services.ai-search.description': 'Search, language models and data pipelines – built on what we learn in our own lab, sized for what you actually need.',
  'services.ai-search.feature1': 'Search & full-text indexing',
  'services.ai-search.feature2': 'Polite web crawling',
  'services.ai-search.feature3': 'Training & evaluating language models',
  'services.ai-search.feature4': 'Data pipelines & dataset curation',
  'services.link.games': 'See the catalogue',
  'services.link.contact': 'Start a project',
  'services.link.lab': 'See the lab',
  'services.tag.ai': 'AI',
  'services.principles.label': 'Working together',
  'services.principles.title': 'What you can expect.',
  'services.principles.lede': 'No guarantees nobody can keep. Just clear agreements and visible progress.',
  'services.principle1.title': 'Clear proposals',
  'services.principle1.text': 'Scope, dates and cost are agreed before we start. Changes are discussed, not slipped in.',
  'services.principle2.title': 'Visible progress',
  'services.principle2.text': 'You see what we are working on on a regular basis – as a build, a demo or a short update. No progress in the dark.',
  'services.principle3.title': 'Honest judgement',
  'services.principle3.text': 'If something is not working, we say so early. Even when it is uncomfortable.',
  'services.cta.label': 'Ready?',
  'services.cta.title': 'What should exist next?',
  'services.cta.lede': 'Your own product or a client project: we listen, ask the right questions and tell you honestly what is possible.',
  'services.cta.button': 'Discuss a project',

  // ── Contact ──────────────────────────────────────────────────
  'contact.label': 'Contact',
  'contact.title': 'Tell us what should exist.',
  'contact.lede': 'A new game, an app, a website or an AI project: a short, honest message is the best start. We reply personally – not with a template.',
  'contact.form.title': 'Your message',
  'contact.form.name': 'Your name *',
  'contact.form.email': 'Email address *',
  'contact.form.subject': 'What is this about? *',
  'contact.form.message': 'Your message *',
  'contact.form.send': 'Send message',
  'contact.form.hint': 'Required fields are marked with *.',
  'contact.form.required': 'Please fill in all required fields.',
  'contact.form.sending': 'Sending …',
  'contact.form.success': 'Thank you! Your message has arrived. We will get back to you personally.',
  'contact.form.error': 'That did not work. Please try again or write directly to info@netbyte.bz.',
  'contact.aside.label': 'Straight to the studio',
  'contact.info.email': 'Email',
  'contact.info.address': 'Address',
  'contact.info.company': 'Company',
  'contact.expect.label': 'Good preparation',
  'contact.expect.title': 'Three things that help.',
  'contact.expect.description': 'You do not need a long brief. These three points are enough for a good start.',
  'contact.expect.item1.title': 'The goal',
  'contact.expect.item1.text': 'What should the product do for people?',
  'contact.expect.item2.title': 'The frame',
  'contact.expect.item2.text': 'When should it start – and what is realistic?',
  'contact.expect.item3.title': 'The state',
  'contact.expect.item3.text': 'Are there sketches, builds or an existing presence?',

  // ── Blog ─────────────────────────────────────────────────────
  'blog.label': 'Studio notes',
  'blog.title': 'Built, measured, written down.',
  'blog.lede': 'What we are working on, the numbers behind it and the mistakes that taught us something.',
  'blog.count': 'Posts',
  'blog.list.label': 'All posts',
  'blog.topic.research': 'Research',
  'blog.readingTime': '{minutes} min read',
  'blog.read': 'Read the article',
  'blog.byline': 'By {author}',
  'blog.contents': 'Contents',
  'blog.back': 'Back to all posts',
  'blog.figure.label': 'Figure {number}',
  'blog.figure.data': 'Show the data',
  'blog.sample.prompt': 'Prompt',
  'blog.sample.output': 'Model output',
  'post.helix-language-model-part-1.title': 'Helix, part 1: training a European language model',
  'post.helix-language-model-part-1.description': 'How we are building Helix, a 320M-parameter language model for all 24 official EU languages: architecture tests, data, bugs and training on gaming GPUs.',

  // ── Press ────────────────────────────────────────────────────
  'press.label': 'Press',
  'press.title': 'Press & media.',
  'press.lede': 'Material, contacts and a short profile for editorial teams writing about NETBYTE.',
  'press.contact.label': 'Press contact',
  'press.contact.role': 'Managing Director',
  'press.updated': 'As of October 2026',
  'press.portrait.label': 'About the studio',
  'press.portrait.title': 'A small studio with its own games and its own technology.',
  'press.portrait.lede': 'NETBYTE makes its own games and technology and builds digital products for clients. The studio is based in Stralsund on the Baltic coast.',
  'press.portrait.text1': 'Our own titles are available for iOS and Android; a PC title is in development. In its lab, NETBYTE is building a language model for the 24 official EU languages and a search engine. For clients we build apps and websites – from the first sketch to release.',
  'press.portrait.text2': 'The Managing Director is {director}. The studio works in small, traceable steps and stays with projects after release.',
  'press.fact.location.label': 'Location',
  'press.fact.location.value': 'Stralsund, Germany',
  'press.fact.management.label': 'Management',
  'press.fact.released.label': 'Released',
  'press.fact.released.value': '{count} titles',
  'press.fact.upcoming.label': 'In development',
  'press.fact.upcoming.value': '{count} PC title',
  'press.boilerplate.label': 'For reuse',
  'press.boilerplate.title': 'Short profile',
  'press.boilerplate.text': 'NETBYTE is an independent technology studio in Stralsund. It develops its own games for iOS, Android and PC and its own AI and search technology, as well as apps and websites for clients. {released} titles are currently live in the stores, with one PC title in development. The Managing Director is {director}.',
  'press.kit.label': 'Press material',
  'press.kit.title': 'Logo and artwork.',
  'press.kit.lede': 'High-resolution image files for editorial coverage of NETBYTE.',
  'press.kit.note': 'Usage questions:',
  'press.kit.logo': 'NETBYTE logo',
  'press.kit.logoAlt': 'NETBYTE logo',
  'press.kit.download': 'Download',
  'press.cta.label': 'Press enquiry',
  'press.cta.title': 'Do you need material or an interview?',
  'press.cta.lede': 'Tell us what you are working on and when you need it. We will reply with an honest assessment.',
  'press.cta.button': 'Send a press enquiry',

  // ── Sustainability ───────────────────────────────────────────
  'sustainability.label': 'Sustainability',
  'sustainability.title': 'Responsibility we can back up.',
  'sustainability.lede': 'We are a small studio. For us, sustainability means using the resources we can actually influence carefully – and not making promises we cannot back up.',
  'sustainability.updated': 'As of October 2026',
  'sustainability.aside.label': 'Transparency',
  'sustainability.aside.note': 'This page names concrete measures. It does not list certificates or CO₂ figures we cannot substantiate.',
  'sustainability.intro.label': 'Approach',
  'sustainability.intro.title': 'Start small, stay honest.',
  'sustainability.intro.text1': 'Our products are digital: games in the stores, websites in the browser. Packaging, physical media and shipping do not apply.',
  'sustainability.intro.text2': 'That does not make us sustainable by default. Digital infrastructure needs power, devices and networks. So we focus on the levers a small studio can actually control.',
  'sustainability.measures.label': 'Measures',
  'sustainability.measures.title': 'What we actually do.',
  'sustainability.measures.lede': 'Four points that follow from how we work and how this website is built.',
  'sustainability.measure1.title': 'Digital distribution',
  'sustainability.measure1.text': 'Our games are released in the App Store and on Google Play, our websites in the browser. There is no physical media and no shipping.',
  'sustainability.measure2.title': 'Lean website',
  'sustainability.measure2.text': 'This site is built statically. Fonts and icons are hosted locally instead of on external CDNs. That reduces external requests and dependencies.',
  'sustainability.measure3.title': 'Little ballast in the code',
  'sustainability.measure3.text': 'No runtime frameworks, no icon library, no external fonts. The bundle stays lean and the page loads without heavy dependencies.',
  'sustainability.measure4.title': 'Themes that stay',
  'sustainability.measure4.text': 'In Eco Tycoon, players build a sustainable empire. That is entertainment, not a sustainability report – but it is a theme we care about.',
  'sustainability.limits.label': 'Limits',
  'sustainability.limits.title': 'What this page is not.',
  'sustainability.limits.text1': 'This page is not a CO₂ balance sheet or a sustainability certificate. We currently lack the measurement basis and the right framework for a credible account.',
  'sustainability.limits.text2': 'We would rather write down what we actually do than invent numbers. If that changes, we will update this page.',
  'sustainability.cta.label': 'Questions?',
  'sustainability.cta.title': 'Let us talk about sustainability.',
  'sustainability.cta.lede': 'If you want to know more about how we work or have an idea for a responsible project, get in touch.',
  'sustainability.cta.button': 'Get in touch',

  // ── Not found ────────────────────────────────────────────────
  'notfound.label': 'Error 404',
  'notfound.title': 'This page is not in the catalogue.',
  'notfound.description': 'The link leads nowhere. The home page and the game catalogue are one click away.',

  // ── Legal: imprint ────────────────────────────────────────────
  'legal.translationNotice': 'This translation is provided for convenience only. The German version is legally binding.',
  'imprint.contact.email': 'Email',
  'imprint.contact.title': 'Contact',
  'imprint.contact.website': 'Website',
  'imprint.copyright.text': 'The content and works created by the site operators on these pages are subject to German copyright law. The reproduction, editing, distribution and any kind of exploitation outside the limits of copyright require the written consent of the respective author or creator.',
  'imprint.copyright.title': 'Copyright',
  'imprint.disclaimer.content.text': 'As a service provider, we are responsible for our own content on these pages in accordance with § 7 para. 1 TMG under general law. According to §§ 8 to 10 TMG, however, we as a service provider are not obligated to monitor transmitted or stored third-party information or to investigate circumstances that indicate illegal activity.',
  'imprint.disclaimer.content.title': 'Liability for Content',
  'imprint.disclaimer.links.text': 'Our website contains links to external third-party websites over whose content we have no influence. Therefore, we cannot assume any liability for this external content. The respective provider or operator of the pages is always responsible for the content of the linked pages.',
  'imprint.disclaimer.links.title': 'Liability for Links',
  'imprint.disclaimer.title': 'Disclaimer',
  'imprint.dispute.note': 'We are not willing or obligated to participate in dispute resolution procedures before a consumer arbitration board.',
  'imprint.dispute.text': 'The European Commission provides a platform for online dispute resolution (ODR):',
  'imprint.dispute.title': 'EU Dispute Resolution',
  'imprint.info.title': 'Information according to § 5 TMG',
  'imprint.page.description': 'Legal provider details, contact information and mandatory disclosures for NETBYTE UG.',
  'imprint.page.eyebrow': 'Company information',
  'imprint.provider.address.city': '18439 Stralsund',
  'imprint.provider.address.country': 'Germany',
  'imprint.provider.address.street': 'Fährhofstraße 12',
  'imprint.provider.company': 'NETBYTE UG (haftungsbeschränkt)',
  'imprint.provider.owner': 'Managing Director',
  'imprint.provider.owner.name': 'Marcus Weitschat',
  'imprint.provider.title': 'Provider',
  'imprint.register.court': 'Registration Court',
  'imprint.register.court.name': 'Amtsgericht Stralsund',
  'imprint.register.number': 'Registration Number',
  'imprint.register.number.value': 'HRB 22972',
  'imprint.register.title': 'Commercial Register',
  'imprint.responsible.content': 'Responsible for content according to § 55 para. 2 RStV',
  'imprint.responsible.title': 'Responsible Person',
  'imprint.summary.title': 'On this page',
  'imprint.tax.number': 'Tax Number',
  'imprint.tax.number.value': '082/115/02364',
  'imprint.tax.title': 'Tax Information',
  'imprint.tax.vat': 'VAT Identification Number',
  'imprint.tax.vat.value': 'DE457016090',
  'imprint.title': 'Imprint',
  'imprint.updated': 'Last updated',

  // ── Legal: privacy policy ─────────────────────────────────────
  'privacy.apps.adid.content': 'For personalized advertising and ad performance measurement, your device advertising identifier (Advertising ID/IDFA) may be processed. You can limit or reset this identifier at any time in your device privacy settings.',
  'privacy.apps.adid.title': 'Advertising ID (Android/iOS)',
  'privacy.apps.admob.content': 'If advertising is enabled in an app, we use Google AdMob to display ads. Personal data may be processed by Google and its partners, for example online identifiers, usage data and approximate location, in order to provide and measure ads.',
  'privacy.apps.admob.title': 'AdMob',
  'privacy.apps.intro': 'When using our mobile applications, additional data processing may occur depending on activated features and your device settings.',
  'privacy.apps.notifications.content': 'Our apps may use local notifications that are generated directly on your device, for example for reminders. No push token or server-based delivery is required for local notifications. You can disable notifications at any time in your device settings.',
  'privacy.apps.notifications.title': 'Local Notifications',
  'privacy.apps.revenuecat.content': 'We use RevenueCat to technically manage in-app purchases and subscriptions. RevenueCat may process purchase-related information such as app user ID, product identifiers, transaction identifiers, subscription status and timestamps. Payment processing is handled by the respective app store provider.',
  'privacy.apps.revenuecat.title': 'RevenueCat and In-App Purchases',
  'privacy.apps.storage.content': 'Our apps may store information locally on your device (for example settings, language, game progress or feature states) to provide core app functionality and improve user experience.',
  'privacy.apps.storage.title': 'Local Storage on Your Device',
  'privacy.apps.title': 'Apps, Advertising and In-App Purchases',
  'privacy.changes.content': 'We reserve the right to adapt this privacy policy so that it always complies with current legal requirements or to implement changes to our services in the privacy policy.',
  'privacy.changes.title': 'Changes to the Privacy Policy',
  'privacy.collection.browser': 'Browser',
  'privacy.collection.content': 'Content of the request (specific page)',
  'privacy.collection.data': 'This data includes:',
  'privacy.collection.datetime': 'Date and time of request',
  'privacy.collection.intro': 'When you merely use the website for informational purposes, i.e. if you do not register or otherwise transmit information to us, we only collect the personal data that your browser transmits to our server.',
  'privacy.collection.ip': 'IP address',
  'privacy.collection.language': 'Language and version of browser software',
  'privacy.collection.os': 'Operating system and its interface',
  'privacy.collection.referrer': 'Website from which the request comes',
  'privacy.collection.status': 'Access status/HTTP status code',
  'privacy.collection.timezone': 'Time zone difference to Greenwich Mean Time (GMT)',
  'privacy.collection.title': 'Collection of Personal Data',
  'privacy.collection.volume': 'Amount of data transferred',
  'privacy.complaint.authority': 'Competent supervisory authority',
  'privacy.complaint.authority.name': 'The State Commissioner for Data Protection and Freedom of Information Mecklenburg-Western Pomerania',
  'privacy.complaint.content': 'You have the right to lodge a complaint with a supervisory authority about our processing of your personal data.',
  'privacy.complaint.title': 'Right to Complaint',
  'privacy.controller.address.city': '18439 Stralsund',
  'privacy.controller.address.country': 'Germany',
  'privacy.controller.address.street': 'Fährhofstraße 12',
  'privacy.controller.company': 'NETBYTE UG (haftungsbeschränkt)',
  'privacy.controller.email': 'Email',
  'privacy.controller.owner': 'Managing Director',
  'privacy.controller.owner.name': 'Marcus Weitschat',
  'privacy.controller.title': 'Data Controller',
  'privacy.cookies.content': 'Our website uses technically necessary cookies to store the language setting. These cookies are required for the proper functioning of the website.',
  'privacy.cookies.settings': 'You can set your browser to be informed about the setting of cookies and only allow cookies on a case-by-case basis.',
  'privacy.cookies.title': 'Cookies',
  'privacy.dataprotection.title': 'Data Protection according to GDPR',
  'privacy.dataprotection.updated': 'Last updated: May 5, 2026',
  'privacy.general.content1': 'This privacy policy informs you about the type, scope and purpose of processing personal data within our online service and the associated websites, functions and content as well as external online presences.',
  'privacy.general.content2': 'With regard to the terminology used, such as "processing" or "controller", we refer to the definitions in Article 4 of the General Data Protection Regulation (GDPR).',
  'privacy.general.title': 'General Information',
  'privacy.legal.content': 'The processing of this data is based on Article 6 paragraph 1 lit. f GDPR from our legitimate interest in the proper functionality of the website and the improvement of our offer.',
  'privacy.legal.title': 'Legal Basis',
  'privacy.page.description': 'How NETBYTE handles website, contact and app-related data under GDPR requirements.',
  'privacy.page.eyebrow': 'Data protection and transparency',
  'privacy.rights.contact': 'To exercise your rights, you can contact us at info@netbyte.bz.',
  'privacy.rights.erasure': 'Right to erasure (Art. 17 GDPR)',
  'privacy.rights.info': 'Right to information (Art. 15 GDPR)',
  'privacy.rights.intro': 'You have the following rights:',
  'privacy.rights.object': 'Right to object (Art. 21 GDPR)',
  'privacy.rights.portability': 'Right to data portability (Art. 20 GDPR)',
  'privacy.rights.rectification': 'Right to rectification (Art. 16 GDPR)',
  'privacy.rights.restriction': 'Right to restriction of processing (Art. 18 GDPR)',
  'privacy.rights.title': 'Your Rights',
  'privacy.security.content': 'We use the widespread SSL procedure (Secure Socket Layer) in connection with the highest encryption level supported by your browser during website visits.',
  'privacy.security.title': 'Data Security',
  'privacy.summary.title': 'Privacy sections',
  'privacy.title': 'Privacy Policy',
  'privacy.updated': 'Last updated: May 5, 2026',

  // ── Legal: terms of service ───────────────────────────────────
  'terms.confidentiality.content': 'NETBYTE UG commits to confidentiality regarding all client information disclosed during the business relationship.',
  'terms.confidentiality.continuation': 'This obligation continues after termination of the contractual relationship.',
  'terms.confidentiality.title': 'Confidentiality',
  'terms.contract.offers': 'All offers from NETBYTE UG are non-binding. Contracts are concluded when the customer accepts the offer in writing or by email.',
  'terms.contract.prices': 'Prices are quoted in euros excluding VAT. The applicable VAT rate is added to the net price.',
  'terms.contract.title': 'Contract Conclusion and Prices',
  'terms.cooperation.access': 'The customer ensures that NETBYTE UG has access to all necessary systems and data.',
  'terms.cooperation.customer': 'The customer undertakes to provide all necessary information and materials required for the execution of the contract in a timely manner.',
  'terms.cooperation.title': 'Cooperation Obligations',
  'terms.copyright.ownership': 'All copyrights and usage rights to the work results created by NETBYTE UG remain with NETBYTE UG until full payment has been made.',
  'terms.copyright.title': 'Copyright and Usage Rights',
  'terms.copyright.transfer': 'Upon full payment, the customer receives the agreed usage rights to the work results.',
  'terms.dataprotection.content': 'NETBYTE UG processes personal data in accordance with the General Data Protection Regulation (GDPR) and other applicable data protection laws.',
  'terms.dataprotection.more': 'Further information on data protection can be found in our privacy policy.',
  'terms.dataprotection.title': 'Data Protection',
  'terms.final.invalidity': 'Should individual provisions of these terms be invalid, the validity of the remaining provisions remains unaffected.',
  'terms.final.law': 'German law applies. The place of jurisdiction is Stralsund, provided the client is a merchant, legal entity under public law, or special fund under public law.',
  'terms.final.title': 'Final Provisions',
  'terms.header.subtitle': 'Terms of Service for NETBYTE UG (limited liability)',
  'terms.liability.content': 'NETBYTE UG\'s liability is limited to intent and gross negligence. For slight negligence, NETBYTE UG is only liable for breach of essential contractual obligations.',
  'terms.liability.dataloss': 'Liability for data loss is limited to the typical recovery effort that would have occurred with regular data backup.',
  'terms.liability.title': 'Liability',
  'terms.page.description': 'The commercial terms for software, app, game and web development services by NETBYTE UG.',
  'terms.page.eyebrow': 'Service terms',
  'terms.provider.country': 'Germany',
  'terms.provider.director': 'Managing Director',
  'terms.provider.director.name': 'Marcus Weitschat',
  'terms.provider.scope': 'These General Terms and Conditions (GTC) apply to all contracts concluded between NETBYTE UG (haftungsbeschränkt) and its customers for the provision of services in the field of software development and digital solutions.',
  'terms.provider.title': 'Provider and Scope of Application',
  'terms.services.description': 'NETBYTE UG (haftungsbeschränkt) offers the following services:',
  'terms.services.list': 'Web development and design, Mobile app development, Digital consulting, Game development',
  'terms.services.title': 'Scope of Services',
  'terms.summary.title': 'Terms sections',
  'terms.title': 'Terms of Service',
  'terms.updated': 'Last updated: January 2025',
  'terms.warranty.content': 'NETBYTE UG warrants the contractual quality of the services provided. The warranty period is one year from acceptance.',
  'terms.warranty.rectification': 'In case of defects, the client first has the right to rectification. If rectification fails, the client may demand reduction or withdrawal.',
  'terms.warranty.title': 'Warranty',
};
